// Inline workspace images referenced by agent-generated HTML (e.g. <img src="chart.png"> next to report.html),
// so the isolated preview can show them: the preview origin has no access to our API, and its CSP only
// allows data:/blob: images. Pure string processing so it runs the same in the browser and in tests.

export const IMAGE_LIMITS = { perImage: 5 * 1024 * 1024, total: 20 * 1024 * 1024 };

const IMAGE_MIME: Record<string, string> = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  svg: "image/svg+xml",
  avif: "image/avif",
  bmp: "image/bmp"
};

export interface InlineImageLabels {
  tooLarge: (name: string) => string;
  unavailable: (name: string) => string;
}

/** Workspace path of a relative image reference, or null when it is not a local file we may load. */
export function resolveRelativeImage(src: string, htmlPath: string): string | null {
  const raw = src.trim().split(/[?#]/, 1)[0];
  if (!raw || raw.startsWith("/") || /^[a-z][a-z\d+.-]*:/i.test(raw)) return null; // absolute, data:, http:, blob:…
  let decoded: string;
  try {
    decoded = decodeURIComponent(raw).replaceAll("\\", "/");
  } catch {
    return null;
  }
  const parts = htmlPath.split("/").slice(0, -1);
  for (const part of decoded.split("/")) {
    if (!part || part === ".") continue;
    if (part === "..") {
      if (parts.length === 0) return null; // escapes the workspace
      parts.pop();
    } else {
      parts.push(part);
    }
  }
  const path = parts.join("/");
  const extension = path.slice(path.lastIndexOf(".") + 1).toLowerCase();
  return path && IMAGE_MIME[extension] ? path : null;
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}

function placeholder(text: string): string {
  return (
    '<span role="note" style="display:inline-block;max-width:100%;box-sizing:border-box;padding:12px 16px;' +
    'border:1px dashed #9ca3af;border-radius:8px;color:#4b5563;background:#f9fafb;font:14px/1.5 system-ui,sans-serif">' +
    escapeHtml(text) +
    "</span>"
  );
}

async function toDataUrl(blob: Blob, mime: string): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = "";
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return `data:${mime};base64,${btoa(binary)}`;
}

const IMG_TAG = /<img\b[^>]*>/gi;
const SRC_ATTR = /(\ssrc\s*=\s*)("([^"]*)"|'([^']*)'|([^\s"'>]+))/i;
const SRCSET_ATTR = /\ssrcset\s*=\s*("[^"]*"|'[^']*'|[^\s"'>]+)/i;

/**
 * Replace relative <img src> references with data: URLs loaded through `load(path)`.
 * Images over the limits or that fail to load become a visible note instead of a broken icon.
 * `srcset` is dropped on inlined images (it would point at the same local files).
 */
export async function inlineLocalImages(
  html: string,
  htmlPath: string,
  load: (path: string) => Promise<Blob>,
  labels: InlineImageLabels,
  limits = IMAGE_LIMITS
): Promise<string> {
  const tags = [...html.matchAll(IMG_TAG)];
  const cache = new Map<string, Promise<string | { error: "tooLarge" | "unavailable" }>>();
  let total = 0;

  const fetchOne = (path: string) => {
    let pending = cache.get(path);
    if (!pending) {
      pending = load(path)
        .then(async (blob) => {
          if (blob.size > limits.perImage || total + blob.size > limits.total) return { error: "tooLarge" as const };
          total += blob.size;
          return toDataUrl(blob, IMAGE_MIME[path.slice(path.lastIndexOf(".") + 1).toLowerCase()]);
        })
        .catch(() => ({ error: "unavailable" as const }));
      cache.set(path, pending);
    }
    return pending;
  };

  const replacements: string[] = [];
  for (const [tag] of tags) {
    const src = SRC_ATTR.exec(tag);
    const value = src ? (src[3] ?? src[4] ?? src[5] ?? "") : "";
    const path = src ? resolveRelativeImage(value, htmlPath) : null;
    if (!src || !path) {
      replacements.push(tag);
      continue;
    }
    const result = await fetchOne(path);
    const name = path.split("/").pop()!;
    if (typeof result !== "string") {
      replacements.push(placeholder(result.error === "tooLarge" ? labels.tooLarge(name) : labels.unavailable(name)));
      continue;
    }
    replacements.push(tag.replace(SRC_ATTR, (_m, prefix: string) => `${prefix}"${result}"`).replace(SRCSET_ATTR, ""));
  }

  let index = 0;
  return html.replace(IMG_TAG, () => replacements[index++]);
}
